import React, { useEffect, useState } from "react";
import css from "./EnquiryForm.module.scss";
import EnquiryFormMessageArea from "./EnquiryFormMessageArea";
import EnquiryFormUserDetails from "./EnquiryFormUserDetails";
import { Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useForm, SubmitHandler, FormProvider } from "react-hook-form";
import { EnquiryFormType } from "./types";
import { ChatIcon } from "@assets/icons";
import { FontAwesomeIcon as Icon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/pro-light-svg-icons";
import useUser from "@hooks/useUser";
import { getCookieByName } from "@helper/cookies";
import { USER_DISPLAY_NAME_COOKIE, USER_EMAIL_COOKIE, USER_GUESTS_COOKIE, USER_PHONE_COOKIE, USER_WEDDING_DATE_COOKIE } from "@helper/constants";

interface Props {
    supplierName: string,
    showMasterVenue: boolean,
    isEngagementVenue: boolean,
    listenSpecialEventClick?: boolean,
    isSupplierMessaging?: boolean,
    hideGuest?: boolean,
    onSubmit: (data: EnquiryFormType, masterVenueUpdated: boolean) => Promise<void>;
    textOptions: {
        tags: string[];
        suggestions: Record<string, string>;
    }
}

const EnquiryForm = ({
    supplierName,
    showMasterVenue,
    isEngagementVenue,
    hideGuest,
    isSupplierMessaging,
    listenSpecialEventClick,
    textOptions,
    onSubmit
}: Props) => {
    const [masterVenueUpdated, setMasterVenueUpdated] = useState<boolean>(false);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const { user } = useUser();
    const methods = useForm<EnquiryFormType>({
        mode: 'onBlur'
    });
    const { setValue, formState: { isLoading }, handleSubmit } = methods;
    useEffect(() => {
        if(!!user?.id) {
            setValue("name", user.name);
            setValue("email", user.email);
            user.phone && setValue("phone", user.phone);
            !!user.guests && setValue("guests", user.guests);
            if(user.weddingDate) {
                const date = (new Date(user.weddingDate)).toISOString().split('T')[0];
                // @ts-ignore-next-line
                setValue("date", date);
            }
        }
        else {
            const cookieFields = {
                name: USER_DISPLAY_NAME_COOKIE,
                email: USER_EMAIL_COOKIE,
                phone: USER_PHONE_COOKIE,
                guests: USER_GUESTS_COOKIE,
                date: USER_WEDDING_DATE_COOKIE,
            };

            Object.entries(cookieFields).forEach(([field, cookieName]) => {
                const cookieValue = getCookieByName(cookieName) || "";
                setValue(field as keyof EnquiryFormType, cookieValue);
            });
        }
    }, [user]);

    const onFormSubmit: SubmitHandler<EnquiryFormType> = async (data) => {
        setSubmitting(true);
        await onSubmit(data, masterVenueUpdated);
        setSubmitting(false);
    }
    const preventFormSubmitOnEnter = (
        event: React.KeyboardEvent<HTMLFormElement>
    ) => {
        if (event.key === "Enter") {
            event.preventDefault();
        }
    };

    return (
        <FormProvider {...methods}>
            <form
                className={css.container}
                onSubmit={handleSubmit(onFormSubmit)}
                onKeyDown={preventFormSubmitOnEnter}
            >
                <div className={css.header}>
                    <div className={css.firstline}>{isSupplierMessaging ? 'SEND A MESSAGE TO' : 'ENQUIRE WITH'}</div>
                    <div className={css.supplier}>{supplierName}</div>
                    <div className={css.secondline}>
                        {isSupplierMessaging && <ChatIcon />}
                        Typically responds within 24h
                    </div>
                </div>
                <div className={css.userData}>
                    <EnquiryFormMessageArea
                        listenSpecialEventClick={listenSpecialEventClick}
                        tags={textOptions.tags}
                        suggestions={textOptions.suggestions}
                        defaultSuggestion={''}
                    />
                    <EnquiryFormUserDetails
                        hideGuest={hideGuest}
                        isEngagementVenue={isEngagementVenue}
                        showMasterVenue={showMasterVenue}
                        setMasterVenueUpdated={setMasterVenueUpdated}
                    />
                    <div className={css.cta}>
                        {isSupplierMessaging && <div className={`small pb-3 ${css.whyEW}`}>
                            <OverlayTrigger overlay={<Tooltip className={css.tooltip}>
                                    <ul>
                                    <li>Track all of your wedding supplier conversations and bookings in one place.</li>
                                    <li>Enquire with new suppliers in just one click</li>
                                    <li>Chat with suppliers on the go with our convenient and free mobile app</li>
                                    <li>Fast response times from verified and top rated local suppliers.</li>
                                    </ul>
                                </Tooltip>
                            }>
                                <Icon icon={faInfoCircle}/>
                            </OverlayTrigger>

                            Why use Easy Weddings to message suppliers?
                        </div>}
                        <Button type="submit" variant="primary" className="w-100" disabled={(isLoading || submitting)} data-testid="enquiry-form-submit">
                            {(isLoading || submitting) ? "Sending" : "Request Quote"}
                        </Button>
                        <div className="small submit-privacy lh-base">
                            By submitting this form you are registering and agreeing to
                            the{" "}
                            <a href="/privacy/" className="text-primary" rel="nofollow">
                                Terms and Conditions.
                            </a>{" "}
                            and{" "}
                            <a href="/privacy/" className="text-primary" rel="nofollow">
                                Privacy Policy.
                            </a>
                        </div>
                    </div>
                </div>
            </form>
        </FormProvider>
    );
}

export default EnquiryForm;
